// Homepage banner slideshow: crossfades slides (or slides them sideways with data-effect="slide"), autoplays (unless reduced motion), and pauses on hover, focus, or the pause button.
class SHeroSlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.dots = [...this.querySelectorAll('[data-slide-dot]')];
    this.pauseButton = this.querySelector('[data-slide-pause]');
    this.index = 0;
    this.interval = Number(this.dataset.interval || 6000);
    this.userPaused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.hoverPaused = false;

    this.querySelector('[data-slide-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-slide-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
    this.pauseButton?.addEventListener('click', () => {
      this.userPaused = !this.userPaused;
      this.sync();
    });

    this.addEventListener('pointerenter', () => { this.hoverPaused = true; this.sync(); });
    this.addEventListener('pointerleave', () => { this.hoverPaused = false; this.sync(); });
    this.addEventListener('focusin', () => { this.hoverPaused = true; this.sync(); });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) { this.hoverPaused = false; this.sync(); }
    });

    let startX = null;
    this.addEventListener('touchstart', (event) => { startX = event.touches[0].clientX; }, { passive: true });
    this.addEventListener('touchend', (event) => {
      if (startX === null) return;
      const delta = event.changedTouches[0].clientX - startX;
      startX = null;
      if (Math.abs(delta) < 40) return;
      const rtl = document.documentElement.dir === 'rtl';
      this.go(this.index + ((delta < 0) !== rtl ? 1 : -1));
    });

    this.render();
    this.sync();
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  go(target) {
    const count = this.slides.length;
    const previous = this.index;
    const direction = target === previous ? 0 : (target > previous ? 1 : -1);
    this.index = (target + count) % count;
    if (this.index === previous) return this.sync();
    this.render(previous, direction);
    this.sync();
  }

  // data-effect="slide": slides move sideways instead of crossfading.
  slideTo(previous, direction) {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rtl = document.documentElement.dir === 'rtl' ? -1 : 1;
    const transition = reduced ? 'none' : 'transform 700ms cubic-bezier(0.22, 1, 0.36, 1)';
    this.slides.forEach((slide, i) => {
      slide.classList.remove('opacity-0', 'invisible');
      const active = i === this.index;
      const leaving = i === previous && direction !== 0;
      if (!active && !leaving) {
        slide.style.transition = 'none';
        slide.style.transform = 'translateX(100%)';
        slide.style.visibility = 'hidden';
      }
    });
    const incoming = this.slides[this.index];
    incoming.style.visibility = 'visible';
    if (direction !== 0) {
      incoming.style.transition = 'none';
      incoming.style.transform = `translateX(${direction * rtl * 100}%)`;
      void incoming.offsetWidth;
      const outgoing = this.slides[previous];
      outgoing.style.transition = transition;
      outgoing.style.transform = `translateX(${-direction * rtl * 100}%)`;
    }
    incoming.style.transition = transition;
    incoming.style.transform = 'translateX(0)';
  }

  render(previous = this.index, direction = 0) {
    if (this.dataset.effect === 'slide') this.slideTo(previous, direction);
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      if (this.dataset.effect !== 'slide') {
        slide.classList.toggle('opacity-0', !active);
        slide.classList.toggle('invisible', !active);
      }
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', String(!active));
      if (active) {
        slide.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      }
    });
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
  }

  sync() {
    clearInterval(this.timer);
    if (this.pauseButton) {
      this.pauseButton.setAttribute('aria-pressed', String(this.userPaused));
      this.pauseButton.setAttribute('aria-label', this.userPaused ? this.dataset.playLabel : this.dataset.pauseLabel);
    }
    if (this.userPaused || this.hoverPaused || this.slides.length < 2) return;
    this.timer = setInterval(() => this.go(this.index + 1), this.interval);
  }
}

if (!customElements.get('s-hero-slideshow')) {
  customElements.define('s-hero-slideshow', SHeroSlideshow);
}
